import { DeleteIconButton, Form, notify } from '@/components';
import {
  ManualPriceDetailsState,
  PersonalizationItemState,
  useGetManualPriceQuery,
  useSavePersonalizationItemManualPriceMutation,
} from '@/redux';
import { Alert, Button, Modal } from 'antd';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';
import {
  formatters,
  getItemTotalPriceBySalesTaxType,
  PersonalizationItemPricingDataDto,
  PricingType,
  SalesTaxType,
} from '@/core';
import { useField } from 'formik';

interface Props {
  item: PersonalizationItemState;
  onClose: () => any;
  type: PricingType;
  salesTaxType?: SalesTaxType;
}

const SCHEMA = Yup.object().shape({
  amount: Yup.number().nullable().defined(),
});

type FormValue = OmitIndex<Yup.InferType<typeof SCHEMA>>;

function useSubmit(props: Props) {
  const { item, onClose, type } = props;
  const [save] = useSavePersonalizationItemManualPriceMutation();
  return useCallback(
    ({ amount }: FormValue) =>
      save({
        id: item.id,
        amount: amount,
        type,
      })
        .unwrap()
        .then(() => {
          notify.success.t('personalization.summary.manualPrice.saved');
          onClose();
        }),
    [save, item.id, type, onClose],
  );
}

export function getManualPriceByType(
  type: PricingType,
  pricing: PersonalizationItemPricingDataDto,
  salesTaxType: SalesTaxType | undefined,
  pricePerFoot: boolean,
): number | undefined {
  const {
    totalBaseCost,
    totalOwnerPrice,
    baseCost,
    ownerPrice,
    residentPrice,
  } = pricing;

  const valueByType: Record<PricingType, number | undefined> = {
    Resident: pricePerFoot
      ? residentPrice
      : getItemTotalPriceBySalesTaxType(pricing, salesTaxType),
    Owner: pricePerFoot ? ownerPrice : totalOwnerPrice,
    Base: pricePerFoot ? baseCost : totalBaseCost,
  };

  return valueByType[type];
}

function useInitialValue(
  props: Props,
  pricePerFoot: boolean,
  isOverridden: boolean,
): FormValue & { defaultValue?: number | null } {
  const { type, salesTaxType, item } = props;

  return useMemo(() => {
    const defaultValue = item.pricing
      ? getManualPriceByType(type, item.pricing, salesTaxType, pricePerFoot)
      : null;

    return {
      amount: isOverridden ? defaultValue ?? null : null,
      defaultValue,
    };
  }, [isOverridden, item.pricing, type, salesTaxType, pricePerFoot]);
}

function isManualPriceOverridden(
  type: PricingType,
  details?: ManualPriceDetailsState | null,
): boolean {
  if (!details) {
    return false;
  }

  switch (type) {
    case 'Base':
      return details.isBaseCostOverridden === true;

    case 'Owner':
      return details.isOwnerPriceOverridden === true;

    case 'Resident':
      return details.isResidentPriceOverridden === true;

    default:
      return false;
  }
}

function ManualPriceAmountField(props: {
  name: string;
  label: string;
  placeholder?: string;
  isOverridden: boolean;
}) {
  const { name, label, placeholder, isOverridden } = props;
  const [{ value }, , helpers] = useField<number | null>(name);

  return (
    <div className="flex-grow-1">
      <Form.Number
        name={name}
        label={label}
        allowClearValue
        placeholder={placeholder}
        suffix={
          <DeleteIconButton
            onClick={() => helpers.setValue(null)}
            disabled={isOverridden ? !value : false}
          />
        }
      />
    </div>
  );
}

export function ManualPriceModalPanel(props: Props) {
  const { onClose, item } = props;
  const { t } = useTranslation(undefined, {
    keyPrefix: 'personalization.summary.manualPrice',
  });

  const { data: details } = useGetManualPriceQuery({
    personalizationItemId: item.id,
  });

  const handleSubmit = useSubmit(props);
  const pricePerFoot = item.pricePer === 'Foot';
  const isOverridden = isManualPriceOverridden(props.type, details);

  const initialValue = useInitialValue(props, pricePerFoot, isOverridden);

  return (
    <Modal
      visible
      onCancel={onClose}
      title={t('title')}
      footer={[
        <Button key="back" type="default" onClick={onClose}>
          {t('cancel')}
        </Button>,
        <Form.Submit uid="manual-price" key="submit" type="primary">
          {t('save')}
        </Form.Submit>,
      ]}
    >
      <Form.Formik<FormValue>
        uid="manual-price"
        i18n="personalization.summary.manualPrice"
        validationSchema={SCHEMA}
        onSubmit={handleSubmit}
        initialValues={initialValue}
        enableReinitialize
      >
        <ManualPriceAmountField
          name="amount"
          label={t(pricePerFoot ? 'amountPerFoot' : 'amount')}
          placeholder={
            initialValue.defaultValue
              ? formatters.number(initialValue.defaultValue)
              : undefined
          }
          isOverridden={isOverridden}
        />
        {isOverridden && <FormAmountChangedBlurb name="amount" />}
      </Form.Formik>
    </Modal>
  );
}

function FormAmountChangedBlurb(props: { name: string }) {
  const { name } = props;
  const [{ value }] = useField<number | null>(name);
  const { t } = useTranslation();

  const showWarning = value == null;

  if (!showWarning) {
    return null;
  }

  return (
    <Alert
      type="warning"
      showIcon
      message={t('personalization.summary.manualPrice.clearWarning')}
      className="mt-2"
    />
  );
}
